import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from 'src/core/services/alert.service';
import { DirtinessService } from 'src/core/services/dirtiness.service';
import { PrescriptionService } from 'src/core/services/prescription.service';
import { BatchStatus } from 'src/interfaces/batch';
import { DeliveryType } from 'src/interfaces/delivery-type';
import { IndividualType } from 'src/interfaces/individual';
import { Order, OrderSplitPayload, Split } from 'src/interfaces/order';
import { OrderItem, OrderItemStatus } from 'src/interfaces/order-item';

@Component({
    selector: 'app-order-split',
    templateUrl: './order-split.component.html',
    styleUrls: ['./order-split.component.scss'],
    standalone: false
})
export class OrderSplitComponent implements OnInit {
  order: Order | null = null;
  orderItems: OrderItem[] = [];
  newOrders: Order[] = [];
  @ViewChild('stepper') private _stepper: MatStepper;

  orderSplitForm = this.formBuilder.group({
    numberOfOrders: ['', [Validators.required, Validators.min(2)]]
  });

  deliveryTypes: DeliveryType[] = [];
  individualTypes: IndividualType[] = [];
  batchStatuses: BatchStatus[] = [];
  orderItemStatuses: OrderItemStatus[] = [];
  splits: Split[] = [];
  connectedTo: string[] = [];
  isEditable: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private prescriptionService: PrescriptionService,
    private router: Router,
    private alertService: AlertService,
    private _dirtinessService: DirtinessService,
  ) {
  }

  ngOnInit(): void {

    this
      .route
      .data
      .subscribe(data => {
        this.order = data.order.order;
        if (!this.order) return;
        this.updateView(this.order);
      });


    this
      .prescriptionService
      .getFormData([
        'delivery_type',
        'individual_type',
        'order_item_status',
        'batch_status'
      ])
      .subscribe(data => {
        this.individualTypes = data.individual_type;
        this.deliveryTypes = data.delivery_type;
        this.orderItemStatuses = data.order_item_status;
        this.batchStatuses = data.batch_status;
      });

    this
      .orderSplitForm
      .valueChanges
      .subscribe(value => {
        // Reset number of splits
        this.splits = [];
      });

  }

  // Workaround for angular component issue #13870
  disableAnimation = true;

  split() {
    const numberOfSplits: number = parseInt(this.orderSplitForm.value.numberOfOrders);
    if (!this.order) {
      this.alertService.errorAlert("An error occurred, could not get order")
      return;
    }

    for (let i = 1; i <= numberOfSplits; i++) {
      if (i === 1) {
        const split: Split = {
          id: i.toString(),
          orderItems: this.getOrderItems(this.order)
        }
        this.splits.push(split);
        this.connectedTo.push(split.id.toString());
      } else {
        const split: Split = {
          id: i.toString(),
          orderItems: []
        }
        this.splits.push(split);
        this.connectedTo.push(split.id.toString());
      }
    }
    this._dirtinessService.setDirty(true);
    this._stepper.next();
    //setTimeout(() => this.disableAnimation = false);
  }

  get isValid(): boolean {
    if (!this.order) return false;
    return this.splits.every(split => split.orderItems.length >= 1)
  }

  async save() {
    const parentOrder: Order | null = this.order;
    const payload: OrderSplitPayload = {
      parentOrderItemIDs: this.splits[0]
        .orderItems
        .map(orderItem => orderItem.id) as number[],
      childOrdersMatrix: []
    }
    if (!parentOrder) return;

    const copySplits = [...this.splits];

    const childSplits = copySplits.slice(1);

    for (const split of childSplits) {
      const row: number[] = split.orderItems.map(item => item.id) as number[];
      payload.childOrdersMatrix.push(row);
    }

    this.newOrders = await this.prescriptionService
      .splitOrder(parentOrder.id, payload); 
    if (this.newOrders.length > 1) {
      this._stepper.next();
      this.isEditable = false;
      this._dirtinessService.setDirty(false);
    }
  }

  updateView(order: Order | null) {
    if (!order) return;
    this.orderItems = this.getOrderItems(order);
  }

  getOrderItems(order: Order) {
    if (!order) return [];

    return order
      .order_items
      .map((item: any) => {

        const orderedItem = {
          id: item.id,
          medicineDosagePackageType: item.medicine_dosage_package_type,
          dosageType: item.medicine_dosage_package_type.medicine_dosage_type,
          status: item.status,
          medicine: item.medicine_dosage_package_type.medicine_dosage_type.medicine,
          quantity: item.total_quantity,
          medicine_dosage_package_type_id: item.medicine_dosage_package_type_id,
          quantity_fulfilled: item.quantity_fulfilled,
          deleted_at: undefined
        }

        orderedItem.dosageType.default_price = item.medicine_dosage_package_type.medicine_dosage_type.default_price;

        return orderedItem;
      });
  }

  addOrderItem(item: OrderItem) {
    this.orderItems.push(item);
    this.orderItems = [
      ...this.orderItems
    ]
  }

  removeOrderItem(item: OrderItem) {

    const index = this.orderItems.indexOf(item);

    if (index > -1)
      this.orderItems.splice(index, 1);
    this.orderItems = [
      ...this.orderItems
    ]
  }

  drop(event: CdkDragDrop<OrderItem[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }

  openOrder(order: Order) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/portal/prescriptions/review/${order.id}`])
    );
    window.open(url, '_blank');
  }

}
